import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * OPTIMIZED: Now uses centralized window.productVariantData for better performance
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 * @property {HTMLElement} badgeContainer - The container for badge display
 * @property {HTMLElement} badgeValue - The element that holds the badge
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku', 'badgeContainer', 'badgeValue'];

  /** @type {Object | null} Cached variant data for performance */
  #variantDataCache = null;

  /** @type {Map<string, string>} Cached badge HTML to avoid re-generating */
  #badgeHtmlCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
    
    // Clear caches on disconnect
    this.#variantDataCache = null;
    this.#badgeHtmlCache.clear();
  }

  /**
   * Get variant data from centralized source with caching
   * @param {string} productId - The product ID
   * @param {string} variantId - The variant ID
   * @returns {Object | null}
   */
  #getVariantData(productId, variantId) {
    // Return cached data if it's for the same variant
    if (this.#variantDataCache?.id === variantId) {
      return this.#variantDataCache;
    }

    // Get from centralized window.productVariantData
    const variantData = window.productVariantData?.[productId]?.variants?.[variantId];
    
    if (variantData) {
      // Cache it for next access
      this.#variantDataCache = variantData;
      return variantData;
    }

    return null;
  }

  /**
   * Get or generate badge HTML with caching
   * @param {string} badgeText - The badge text
   * @returns {string} The badge HTML
   */
  #getBadgeHtml(badgeText) {
    // Check cache first
    if (this.#badgeHtmlCache.has(badgeText)) {
      return this.#badgeHtmlCache.get(badgeText);
    }

    // Generate badge HTML
    let badgeClass = 'badge';
    
    // Logic matching Liquid case statement
    switch (badgeText) {
      case 'New': badgeClass += ' badge--new'; break;
      case 'Best Seller': badgeClass += ' badge--bestseller'; break;
      case 'Open Box': badgeClass += ' badge--openbox'; break;
      case 'Clearance': badgeClass += ' badge--clearance'; break;
      case 'Last Chance': badgeClass += ' badge--clearance'; break;
      case 'Top10': badgeClass += ' badge--top10'; break;
      case 'Backorder': badgeClass += ' badge--backorder'; break;
      case 'Pre-order': badgeClass += ' badge--preorder'; break;
      case 'Exclusives': badgeClass += ' badge--exclusives'; break;
      case 'Highly Recommended': badgeClass += ' badge--highlyrecommended'; break;
      case 'Discontinued': badgeClass += ' badge--discontinued'; break;
      case 'Coming Soon': badgeClass += ' badge--coming-soon'; break;
    }

    const html = `<span class="${badgeClass}">${badgeText}</span>`;
    
    // Cache for future use
    this.#badgeHtmlCache.set(badgeText, html);
    
    return html;
  }

  /**
   * Updates the SKU and badge display
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    // Handle product change in combined listings
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    // Ensure this event is for this component's product
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (!event.detail.resource) return;

    const variant = event.detail.resource;
    const productId = this.dataset.productId;
    
    // Get variant data from centralized source (with caching)
    const variantData = this.#getVariantData(productId, variant.id);
    
    // Fallback to event data if centralized data not available
    const variantSku = variantData?.sku || variant.sku || '';
    const badgeText = variantData?.badge || variant.metafields?.custom?.badge || '';

    // 1. Update SKU text
    this.refs.sku.textContent = variantSku;

    // 2. Update Badge HTML based on text value
    if (badgeText) {
      // Use cached badge HTML generation
      this.refs.badgeValue.innerHTML = this.#getBadgeHtml(badgeText);
      this.refs.badgeContainer.style.display = 'flex';
    } else {
      this.refs.badgeContainer.style.display = 'none';
      this.refs.badgeValue.innerHTML = '';
    }

    // 3. Overall Visibility
    this.style.display = (variantSku || badgeText) ? 'block' : 'none';
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}